import { useEffect, useRef, useState } from 'react'
import type { MediaPreviewError } from '../models/device.model'
import { deviceManager } from '../services/device-manager'
import { useDevicesStore } from '../store/devices.store'
import { useMeetingSessionStore } from '@/features/meeting/store/meeting-session.store'
import { mapMediaErrorToPermissionState } from '../utils/device-permissions'
import { isPassiveJoinUrl } from '@/features/meetings/utils/joinLinks'

/**
 * The lobby's view of the camera and microphone.
 *
 * This hook no longer opens anything. It claims the shared devices from
 * `deviceManager` and hands back a stream per kind; the manager decides when
 * hardware actually starts and stops. Two consequences worth knowing:
 *
 *  - **The camera survives the join.** The meeting claims the same track before
 *    this hook releases it, so the device is never closed and re-opened in
 *    between. That removes the black moment and the camera-light blink at the
 *    start of every call, and it means the background compositor is handed a
 *    camera that is already warm.
 *  - **Toggling the mic still cannot disturb the camera.** They were split into
 *    two streams and two effects to make that structurally true rather than
 *    merely absent from a dependency list. That property is kept — each kind is
 *    claimed and released on its own.
 *
 * Turning a device off releases it, and the manager stops the track rather than
 * disabling it, so the camera indicator light goes out. `enabled = false` would
 * leave the device open and the light on, which reads as being watched.
 */

type MediaPreviewOptions = {
  audio?: boolean
  video?: boolean
}

type MediaPreviewResult = {
  audioStream: MediaStream | null
  videoStream: MediaStream | null
  error: MediaPreviewError | null
}

function classifyError(error: unknown): MediaPreviewError {
  if (error instanceof DOMException) {
    if (error.name === 'NotAllowedError') {
      return { kind: 'permission-denied', message: error.message }
    }
    if (error.name === 'NotFoundError') {
      return { kind: 'no-device', message: error.message }
    }
  }
  const message = error instanceof Error ? error.message : 'Unknown media error'
  return { kind: 'unknown', message }
}

export function useMediaPreview(options?: MediaPreviewOptions): MediaPreviewResult {
  const storeMic = useDevicesStore((s) => s.micEnabled)
  const storeCam = useDevicesStore((s) => s.camEnabled)
  const selectedMicId = useDevicesStore((s) => s.selectedMicId)
  const selectedCamId = useDevicesStore((s) => s.selectedCamId)
  const setPermissionState = useDevicesStore((s) => s.setPermissionState)
  const setDevicePermission = useDevicesStore((s) => s.setDevicePermission)

  /**
   * A view-only attendee is never previewed, because there is nothing to
   * preview and asking costs something.
   *
   * This hook is what the prejoin screen and the mic control use, so it is the
   * prompt a passive attendee would actually have seen — before the meeting,
   * for a camera and microphone this meeting will never publish. Worse than
   * pointless: a refusal at that prompt is remembered by the browser for the
   * whole origin, so declining here would leave them without devices in the
   * next meeting they join as a full participant.
   *
   * Expressed as "wants nothing" rather than as an early return, so it takes
   * the same no-request path the hook already has for a user who arrived with
   * both toggles off.
   */
  // The store, OR the address bar as it stands. The store is seeded from the
  // URL at module load, which covers a page opened on a passive link; the live
  // check covers arriving at one through client-side navigation, where the
  // store was initialised against a different URL.
  const viewOnlyState = useMeetingSessionStore((s) => s.viewOnly)
  const viewOnly = viewOnlyState || isPassiveJoinUrl()

  const wantAudio = viewOnly ? false : (options?.audio ?? storeMic)
  const wantVideo = viewOnly ? false : (options?.video ?? storeCam)

  const [audioStream, setAudioStream] = useState<MediaStream | null>(null)
  const [videoStream, setVideoStream] = useState<MediaStream | null>(null)
  const [error, setError] = useState<MediaPreviewError | null>(null)

  // Set once the combined first request has settled. The per-kind effects wait
  // for it so they cannot fire their own single-kind requests first and cause
  // the extra prompt the combined call exists to avoid.
  const [primed, setPrimed] = useState(false)

  // Latest wants, read by the mount-only prime without becoming deps.
  const wants = useRef({ wantAudio, wantVideo })
  wants.current = { wantAudio, wantVideo }

  useEffect(() => {
    let cancelled = false

    const prime = async () => {
      const { wantAudio: a, wantVideo: v } = wants.current
      if (!a && !v) {
        if (!cancelled) setPrimed(true)
        return
      }
      try {
        await deviceManager.prime({ audio: a, video: v })
        if (!cancelled) {
          setError(null)
          setPermissionState('granted')
          if (a) setDevicePermission('audio', 'granted')
          if (v) setDevicePermission('video', 'granted')
        }
      } catch (err) {
        if (!cancelled) {
          setError(classifyError(err))
          const state = mapMediaErrorToPermissionState(err)
          setPermissionState(state)
          // The verdict, recorded per kind — which is what lowers the toggles.
          //
          // Both devices were asked for in one call, so a refusal is a refusal
          // of everything that was asked for; the browser's combined prompt has
          // no way to say otherwise. Recording it here also spares us the
          // per-kind requests below: they would each re-ask and each be refused
          // again, which is the extra prompt the combined call exists to avoid.
          if (a) setDevicePermission('audio', state)
          if (v) setDevicePermission('video', state)
        }
      } finally {
        if (!cancelled) setPrimed(true)
      }
    }

    void prime()
    return () => {
      cancelled = true
    }
    // Mount only: this is the permission-grant request, not a reaction to state.
  }, [setPermissionState, setDevicePermission])

  // Microphone. Deliberately blind to anything about video.
  useEffect(() => {
    if (!primed) return
    if (!wantAudio) {
      setAudioStream(null)
      return
    }

    let released = false
    const unsubscribe = deviceManager.subscribe('audio', (track) => {
      setAudioStream(track ? new MediaStream([track]) : null)
    })

    deviceManager
      .acquire('audio')
      .then((track) => {
        if (released) return
        setAudioStream(track ? new MediaStream([track]) : null)
        setError(null)
        setPermissionState('granted')
        setDevicePermission('audio', 'granted')
      })
      .catch((err) => {
        if (released) return
        setError(classifyError(err))
        const state = mapMediaErrorToPermissionState(err)
        setPermissionState(state)
        setDevicePermission('audio', state)
      })

    return () => {
      released = true
      unsubscribe()
      deviceManager.release('audio')
    }
  }, [primed, wantAudio, setPermissionState, setDevicePermission])

  // Camera. Likewise blind to the microphone.
  useEffect(() => {
    if (!primed) return
    if (!wantVideo) {
      setVideoStream(null)
      return
    }

    let released = false
    const unsubscribe = deviceManager.subscribe('video', (track) => {
      setVideoStream(track ? new MediaStream([track]) : null)
    })

    deviceManager
      .acquire('video')
      .then((track) => {
        if (released) return
        setVideoStream(track ? new MediaStream([track]) : null)
        setError(null)
        setPermissionState('granted')
        setDevicePermission('video', 'granted')
      })
      .catch((err) => {
        if (released) return
        setError(classifyError(err))
        const state = mapMediaErrorToPermissionState(err)
        setPermissionState(state)
        setDevicePermission('video', state)
      })

    return () => {
      released = true
      unsubscribe()
      deviceManager.release('video')
    }
  }, [primed, wantVideo, setPermissionState, setDevicePermission])

  // Device choices, applied to whatever is running. Separate from the claim
  // effects above so picking a different microphone does not release and
  // re-claim the camera on the way past.
  useEffect(() => {
    if (!primed || !wantAudio) return
    void deviceManager.setDevice('audio', selectedMicId || null).catch((err) => {
      setError(classifyError(err))
    })
  }, [primed, wantAudio, selectedMicId])

  useEffect(() => {
    if (!primed || !wantVideo) return
    void deviceManager.setDevice('video', selectedCamId || null).catch((err) => {
      setError(classifyError(err))
    })
  }, [primed, wantVideo, selectedCamId])

  return { audioStream, videoStream, error }
}
